import { setFeedFolderSaleBadge } from '@/helpers/feedFolderSaleBadge';

// フィードフォルダ（連携配信）商品の表示枠を9:16に強制するためのヘルパー。
//
// 通常コンテンツは管理画面から9:16前提でアップロードされるが、フィードフォルダの画像は
// GMCの商品画像（正方形などの任意比率）をそのまま使うため、PCでは
// `width:100% / height:auto`（createSlide.js）により枠自体が商品画像の比率になってしまう。
// 枠を9:16に固定し、画像は contain で中央に置いて余った上下（または左右）を黒の単色で埋める。
export const FEED_FOLDER_ASPECT_RATIO = '9 / 16';
const FEED_FOLDER_ASPECT_VALUE = 9 / 16;

// モバイルは slide / wrapper が 100dvh・`object-fit: contain`・黒背景（createSlide.js）で
// 既に「中央寄せ＋黒帯」になっているため、枠のCSSはPCのみ課す。
const shouldApplyCssFrame = () => !window.storeeelItems?.isMobile;

export const applyFeedFolderAspectFrame = (wrapper, mediaEl) => {
  if (!wrapper) return;

  // CTA の配置基準を通常コンテンツ（9:16前提）と揃えるため、枠のCSSを課さないモバイルでも
  // 「この枠はフィードフォルダ商品を表示している」ことだけは記録する（ctaHostPosition.js が参照）。
  wrapper.dataset.feedFolderContent = 'true';
  if (!shouldApplyCssFrame()) return;

  wrapper.dataset.feedFolderFrame = 'true';
  wrapper.style.width = '100%';
  wrapper.style.aspectRatio = FEED_FOLDER_ASPECT_RATIO;
  wrapper.style.height = '';
  // 余白は黒の単色（createSlide.js でも同色を設定しているが、DOM再利用経路でも確実に黒にする）
  wrapper.style.background = '#000';

  if (!mediaEl) return;
  mediaEl.style.width = '100%';
  mediaEl.style.height = '100%';
  mediaEl.style.objectFit = 'contain';
  mediaEl.style.display = 'block';
};

// 同じ wrapper が通常コンテンツの描画に再利用された場合に、強制した枠を元に戻す。
export const clearFeedFolderAspectFrame = (wrapper, mediaEl) => {
  if (!wrapper?.dataset) return;

  delete wrapper.dataset.feedFolderContent;
  // 枠のCSSを課していない（＝モバイル）場合は、100dvh + contain のレイアウトに手を出さない。
  if (!wrapper.dataset.feedFolderFrame) return;
  delete wrapper.dataset.feedFolderFrame;

  // createSlide.js のPC既定（wrapper: 幅指定なし / mediaEl: width 100% + height auto）へ戻す。
  // 背景の黒は createSlide.js でも常に設定しているため、ここでは戻さない（黒帯の前提を壊さない）。
  wrapper.style.aspectRatio = '';
  wrapper.style.width = '';

  if (!mediaEl) return;
  mediaEl.style.width = '100%';
  mediaEl.style.height = 'auto';
  mediaEl.style.objectFit = '';
};

// content に応じて枠の適用/解除を切り替える（呼び出し側の分岐重複を避ける）。
//
// SALEラベルもここで一緒に同期する。ラベルを出し入れすべきタイミング（スライド生成・
// グループ入場・グループ内送り）は枠の付け外しと完全に同じで、別の関数に分けると
// どちらか一方だけ呼ばれて前の商品のラベルが残る、という取りこぼしが起きるため。
export const syncFeedFolderAspectFrame = (wrapper, content, mediaEl) => {
  setFeedFolderSaleBadge(wrapper, content);

  if (content?.isFeedFolderContent) {
    applyFeedFolderAspectFrame(wrapper, mediaEl);
    return;
  }
  clearFeedFolderAspectFrame(wrapper, mediaEl);
};

// wrapper の内側に9:16の枠を contain で収めた矩形を返す。
// モバイルでは wrapper が画面全体（100dvh）のままなので、CTA の配置基準としてこの仮想枠を使う。
export const resolveFeedFolderFrameRect = (wrapperRect) => {
  if (!wrapperRect) return null;

  const left = Number(wrapperRect.left);
  const top = Number(wrapperRect.top);
  const width = Number.isFinite(wrapperRect.width)
    ? Number(wrapperRect.width)
    : Number(wrapperRect.right) - left;
  const height = Number.isFinite(wrapperRect.height)
    ? Number(wrapperRect.height)
    : Number(wrapperRect.bottom) - top;

  if (!Number.isFinite(left) || !Number.isFinite(top)) return null;
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  if (width <= 0 || height <= 0) return null;

  const frameWidth = Math.min(width, height * FEED_FOLDER_ASPECT_VALUE);
  const frameHeight = frameWidth / FEED_FOLDER_ASPECT_VALUE;
  const offsetX = (width - frameWidth) / 2;
  const offsetY = (height - frameHeight) / 2;

  return {
    left: left + offsetX,
    right: left + offsetX + frameWidth,
    bottom: top + offsetY + frameHeight,
  };
};
